import { useEffect, useState } from "react";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { downloadCSV } from "../../lib/csv";

const iso = (d) => d.toISOString().slice(0, 10);
const COLOR = { confirmado: "bg-[var(--forest)] text-white", reservado: "bg-amber-100", cancelado: "bg-red-50 text-red-800" };

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-ink)]">{label}</p>
    <p data-testid={testid} className="mt-1 font-display text-3xl">{value}</p>
  </div>
);

export const SalesPanel = ({ isAdmin }) => {
  const [range, setRange] = useState([iso(new Date()), iso(new Date())]);
  const [office, setOffice] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/admin/sales", { params: { date_from: range[0], date_to: range[1], office: office || undefined } })
      .then((r) => setD({ items: Array.isArray(r.data?.items) ? r.data.items : [], total: r.data?.total ?? 0, tickets: r.data?.tickets ?? 0, offices: r.data?.offices ?? [] }))
      .catch((e) => toast.error(apiError(e)));
  }, [range, office]);

  const csv = () => downloadCSV(`ventas_${range[0]}_${range[1]}.csv`, [["Código", "Vendido", "Viaje", "Sale", "Desde", "Hasta", "Butacas", "Pasajero", "DNI", "Total", "Estado", "Boletería", "Vendedor"],
    ...d.items.map((b) => [b.code, b.created_at?.slice(0, 16).replace("T", " "), b.date, b.departure, b.from_stop, b.to_stop, b.seats.join(" "), b.name, b.dni, b.total, b.status, b.office, b.seller_name])]);

  return (
    <div data-testid="sales-panel" className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        Vendidos desde <Input data-testid="sales-from" type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="h-9 w-auto" />
        hasta <Input data-testid="sales-to" type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="h-9 w-auto" />
        {isAdmin && (
          <select data-testid="sales-office" value={office} onChange={(e) => setOffice(e.target.value)} className="h-9 rounded-lg border border-black/10 bg-white px-2">
            <option value="">Todas las boleterías y web</option>
            {(d?.offices ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {d?.items.length > 0 && <button onClick={csv} data-testid="sales-csv" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white"><Download className="h-4 w-4" /> Descargar Excel (CSV)</button>}
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Recaudado (confirmados)" value={formatARS(d.total)} testid="sales-total" />
            <Stat label="Pasajes vendidos" value={d.tickets} testid="sales-tickets" />
            <Stat label="Operaciones" value={d.items.length} testid="sales-count" />
          </div>
          <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-black/5">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-[var(--paper)] text-left font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted-ink)]"><tr><th className="p-3">Código</th><th className="p-3">Viaje</th><th className="p-3">Tramo</th><th className="p-3">Butacas</th><th className="p-3">Pasajero</th><th className="p-3">Total</th><th className="p-3">Estado</th>{isAdmin && <th className="p-3">Boletería</th>}</tr></thead>
              <tbody>
                {d.items.length === 0 && <tr><td colSpan={8} className="p-6 text-[var(--muted-ink)]">No hay ventas en estas fechas.</td></tr>}
                {d.items.map((b) => (
                  <tr key={b.id} data-testid={`sale-row-${b.code}`} className="border-t border-black/5">
                    <td className="p-3 font-mono text-xs">{b.code}</td><td className="p-3 font-mono text-xs">{b.date} {b.departure}</td>
                    <td className="p-3">{b.origin} → {b.destination}</td><td className="p-3">{b.seats.join(", ")}</td>
                    <td className="p-3">{b.name}{b.dni && <span className="block text-xs text-[var(--muted-ink)]">DNI {b.dni}</span>}</td>
                    <td className="p-3 font-semibold">{formatARS(b.total)}</td>
                    <td className="p-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${COLOR[b.status] ?? "bg-[var(--paper)]"}`}>{b.status}</span></td>
                    {isAdmin && <td className="p-3 text-xs">{b.office}{b.seller_name && <span className="block text-[var(--muted-ink)]">{b.seller_name}</span>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
